import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, Download, FilePlus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/labels";
import { userName, useUsers } from "../lib/hooks";
import { EmptyState, PageHeader, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const DOC_TYPES = ["contrato", "factura", "informe", "acta", "poliza", "certificado", "anexo", "comunicacion"];

export default function Documentos() {
  const { map } = useUsers();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["documents", q, type],
    queryFn: async () => (await api.get("/documents", { params: { q: q || undefined, doc_type: type || undefined } })).data,
  });

  const download = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch { toast.error("No se pudo abrir el documento"); }
  };

  const newVersion = async (doc) => {
    const notes = window.prompt(`Nueva versión (v${doc.version + 1}) de «${doc.name}». Notas de la versión:`);
    if (notes === null) return;
    try {
      await api.post(`/documents/${doc.id}/version`, { notes });
      toast.success(`Versión v${doc.version + 1} creada (la anterior se conserva)`);
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/archive`);
      toast.success("Documento archivado");
      refetch();
    } catch (e) { toast.error(apiError(e, "No se pudo archivar")); }
  };

  return (
    <div data-testid="documentos-page">
      <PageHeader title="Documentos" subtitle="Gestión documental básica con versionado. Los documentos marcados son sintéticos: DEMO — DOCUMENTO FICTICIO." />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar documento…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-64" data-testid="doc-search-input" />
        <Select value={type} onValueChange={(v) => setType(v === "todos" ? "" : v)}>
          <SelectTrigger className="h-8 w-44" data-testid="doc-type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos los tipos</SelectItem>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando documentos…</p>
      ) : !items?.length ? (
        <EmptyState title="Sin documentos" description="No hay documentos con estos filtros." testid="documents-empty" />
      ) : (
        <div className="ts-card overflow-hidden">
          <Table data-testid="documents-table">
            <TableHeader>
              <TableRow>
                <TableHead>Documento</TableHead>
                <TableHead className="hidden md:table-cell">Expediente</TableHead>
                <TableHead className="hidden lg:table-cell">Propietario</TableHead>
                <TableHead>Versión</TableHead>
                <TableHead className="hidden sm:table-cell">Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`document-row-${d.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{d.name}</p>
                    <p className="text-xs text-slate-400">
                      {d.doc_type} · {d.status} · <span className="font-medium text-red-600">DEMO — DOCUMENTO FICTICIO</span>
                    </p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {d.expediente ? <Link to={`/expedientes/${d.expediente_id}`} className="text-xs text-blue-700 hover:underline">{d.expediente.code}</Link> : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="hidden text-sm text-slate-600 lg:table-cell">{userName(map, d.owner_id)}</TableCell>
                  <TableCell><ToneChip label={`v${d.version}`} tone="info" testid={`doc-version-${d.id}`} /></TableCell>
                  <TableCell className="hidden text-sm text-slate-600 sm:table-cell">{fmtDate(d.date)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`doc-download-${d.id}`} aria-label="Abrir PDF"><Download className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => newVersion(d)} data-testid={`doc-new-version-${d.id}`} aria-label="Nueva versión"><FilePlus className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => archive(d)} data-testid={`doc-archive-${d.id}`} aria-label="Archivar"><Archive className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
